import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useDocumentsHubFilterStore } from '@/features/documents/components/pages/documents-hub-page';
import { useDeleteDocument } from '@/features/documents/hooks/use-delete-document';
import {
  projectDocumentsQueryKey,
  useProjectDocuments,
} from '@/features/documents/hooks/use-documents';
import { NavigationButton } from '@/features/projects/project-details/components/navigation-button';
import PlusIcon from '@/shared/assets/plus.svg?react';
import DocumentCard from '@/shared/components/card-elements/document-card';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { PaginationBar } from '@/shared/components/pagination-bar';
import { NoEntriesCardPlaceholder } from '@/shared/components/placeholders/no-entries-card-placeholder';
import { Section } from '@/shared/components/section/section';
import {
  SectionHeader,
  SectionTitle,
  SectionActions,
} from '@/shared/components/section/section-header';
import { Button } from '@/shared/components/ui/button';
import { DocumentUploadDialog } from '@/shared/components/ui/upload/document-upload-dialog';
import { usePagination } from '@/shared/hooks/use-pagination';
import {
  ProjectDocument,
  ProjectType,
  SecureProjectDocument,
} from '@/shared/types/api';

const PAGE_SIZE = 10;

export const ProjectDetailsDocuments = ({
  projectId,
  projectType,
  partnerName,
}: {
  projectId: number;
  projectType?: ProjectType;
  partnerName?: string;
}) => {
  const { t: tDocument } = useTranslation('document');
  const navigate = useNavigate();
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState<
    SecureProjectDocument | undefined
  >(undefined);

  const {
    data: documents,
    isLoading,
    isError,
  } = useProjectDocuments({ projectId, filters: undefined });

  const { paginatedData, currentPage, totalPages, setCurrentPage } =
    usePagination({ data: documents ?? [], pageSize: PAGE_SIZE });

  const { mutate: deleteDoc } = useDeleteDocument();

  const queryClient = useQueryClient();
  const documentsHubFilterStore = useDocumentsHubFilterStore();

  const onDeleteDocument = (document: SecureProjectDocument) => {
    deleteDoc(
      { documentId: document.id, documentPath: document.file_path },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: projectDocumentsQueryKey(projectId),
          });
        },
      },
    );
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{t('documents')}</SectionTitle>
        <SectionActions>
          <Button
            variant={'greenGradient'}
            btnType="secondary"
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            <PlusIcon />
            {t('create_documents')}
          </Button>
          <NavigationButton
            onClick={() => {
              documentsHubFilterStore.setProjectIds([projectId.toString()]);
              navigate(paths.documents.path);
            }}
          />
        </SectionActions>
      </SectionHeader>
      <div className="flex flex-row gap-8 overflow-x-auto [scrollbar-width:none]">
        {isLoading ? (
          <LoadingIndicator />
        ) : isError || !documents ? (
          <div>{tDocument('Errors.fetch')}</div>
        ) : documents.length == 0 ? (
          <NoEntriesCardPlaceholder
            title={tDocument('no_documents')}
            description={tDocument('no_documents_description')}
            action={() => {
              setDialogOpen(true);
            }}
          />
        ) : (
          paginatedData.map((document, index) => {
            return (
              <DocumentCard
                key={index}
                document={document}
                onEdit={() => {
                  setCurrentDocument(document);
                  setDialogOpen(true);
                }}
                onDelete={() => {
                  onDeleteDocument(document);
                }}
                hasWriteAccess={document.write_access}
                viewingProjectType={projectType}
                viewingPartnerName={partnerName}
              />
            );
          })
        )}
      </div>
      {documents && documents.length > 0 && (
        <PaginationBar
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
      <DocumentUploadDialog
        isOpen={dialogOpen}
        document={
          currentDocument
            ? ({
                association: currentDocument.association,
                created_at: currentDocument.created_at,
                creator_id: currentDocument.creator.id,
                file_path: currentDocument.file_path,
                id: currentDocument.id,
                org_id: currentDocument.org_id,
                project_id: currentDocument.project_id,
                title: currentDocument.title,
                type: currentDocument.type,
                visibility: currentDocument.visibility,
              } as ProjectDocument)
            : undefined
        }
        editMode={!!currentDocument}
        onClose={() => {
          setDialogOpen(false);
          setCurrentDocument(undefined);
        }}
        projectId={projectId}
        onSubmitSuccess={(data) => {
          // Invalidate the new project's documents
          queryClient.invalidateQueries({
            queryKey: projectDocumentsQueryKey(data.project_id),
          });
          // Also invalidate current project's documents in case the document was moved away
          if (data.project_id !== projectId) {
            queryClient.invalidateQueries({
              queryKey: projectDocumentsQueryKey(projectId),
            });
          }
        }}
      />
    </Section>
  );
};
